// src/components/settings/catalog/CatalogSettings.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 2 (screen).
//
// Settings › Catalog. Replaces Settings › Line Items for a company whose switch is on
// (organizations/{org}.catalogScreenEnabled — utils/catalogUi.isCatalogScreenOn; set only by
// functions/scripts/setCatalogScreen.js; on unless explicitly switched off — catroll-5c8e2a71), for
// Owner / Admin / Manager / Office Staff (SettingsPage canSeeCatalog; Office Staff removed 2026-10-23,
// back 2026-10-30 — offcost-2e7c9a14).
// Switch off = the old Line Items page, unchanged.
//
// Stage 2 has: Items (folders, search, list, editor) and Suppliers. Stage 4 (catscreen-7a2c4e91)
// adds Export here, and the bulk bar and favorites on the Items tab. Stage 6 (catscreen-7a2c4e91)
// adds Import (an "Import" button beside Export, and an "Import history" link): each opens its own
// screen in place of the tabs, with "← Back to Catalog". Imports and undos run through the same job
// runner as bulk changes (CatalogJobRunner); after an import, Import history opens so it can be undone.
// Every change goes through the catalog callables (catalogApi.js); the list is server-side search,
// one page at a time, so it works the same for 50 items or 50,000.
import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, Plus, Download, Upload } from 'lucide-react';
import { listenSuppliers } from './catalogApi';
import CatalogItemsTab from './CatalogItemsTab';
import CatalogSuppliersTab from './CatalogSuppliersTab';
import ExportDialog from './CatalogExport';
import CatalogImport from './CatalogImport'; // catscreen-7a2c4e91 Stage 6
import CatalogImportHistory from './CatalogImportHistory';
import { useCatalogJob, JobDialog } from './CatalogJobRunner';
import { BTN_PRIMARY, BTN_GHOST, BTN_LINK } from './catalogUiParts';
import { useTaxCodesDoc } from '../../../utils/taxExemptionUi'; // taxpick4-54573069
import { catalogSalesTaxCodes } from '../../../utils/catalogUi'; // taxpick4-54573069

export default function CatalogSettings({ currentUserProfile, companySettings }) {
    const orgId = currentUserProfile?.organizationId;
    const showTax = companySettings?.quickbooksConnected === true;
    // taxpick4-54573069 — Settings › Taxes on and no QuickBooks: the item editor's "Tax code" box (null = none).
    const taxCodesDoc = useTaxCodesDoc(orgId);
    const salesTaxCodes = catalogSalesTaxCodes(orgId, taxCodesDoc, showTax);
    const [tab, setTab] = useState('items');
    const [newItemSignal, setNewItemSignal] = useState(0);
    const [newSupplierSignal, setNewSupplierSignal] = useState(0);
    const [suppliers, setSuppliers] = useState(null);
    const [suppliersError, setSuppliersError] = useState('');
    const [exportOpen, setExportOpen] = useState(false); // catscreen-7a2c4e91 Stage 4
    // catscreen-7a2c4e91 Stage 6 — 'catalog' | 'import' | 'history'; the import/undo job runner.
    const [view, setView] = useState('catalog');
    const [historySignal, setHistorySignal] = useState(0);
    const [listKey, setListKey] = useState(0);
    const runner = useCatalogJob({
        orgId,
        onFinished: useCallback((result, job) => {
            if (!job) return;
            setListKey((k) => k + 1); // the Items list shows the new prices when you go back
            if (job.action === 'import' || job.action === 'importUndo') { setView('history'); setHistorySignal((n) => n + 1); }
        }, []),
    });

    useEffect(() => {
        if (!orgId) return undefined;
        return listenSuppliers(orgId, (list) => { setSuppliers(list); setSuppliersError(''); }, (err) => {
            console.error('Catalog: suppliers failed', err);
            setSuppliersError("Couldn't load your suppliers. Refresh the page to try again.");
        });
    }, [orgId]);

    if (!orgId) return null;

    if (view === 'import' || view === 'history') {
        return (
            <div className="p-4 sm:p-6">
                {view === 'import'
                    ? <CatalogImport runner={runner} onBack={() => setView('catalog')} onHistory={() => setView('history')} />
                    : <CatalogImportHistory runner={runner} reloadSignal={historySignal} onBack={() => setView('catalog')} onNewImport={() => setView('import')} />}
                <JobDialog runner={runner} />
            </div>
        );
    }

    const tabBtn = (id, label) => (
        <button type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
            className={`min-h-[44px] px-4 text-sm font-medium border-b-2 -mb-px focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5F4EC9]/40 ${
                tab === id ? 'border-[#5F4EC9] text-[#5F4EC9]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            {label}
        </button>
    );

    return (
        <div className="p-4 sm:p-6 space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2"><BookOpen size={20} className="text-[#5F4EC9]" /> Catalog</h2>
                    <p className="text-sm text-gray-500 mt-1">The services, parts and materials you put on estimates and invoices.</p>
                </div>
                {tab === 'items' ? (
                    <div className="flex flex-wrap items-center gap-2">
                        {/* catscreen-7a2c4e91 Stage 6 — Import (a price list) and its history; Stage 4 — Export */}
                        <button type="button" className={BTN_LINK} onClick={() => setView('history')}>Import history</button>
                        <button type="button" className={BTN_GHOST} onClick={() => setView('import')}><Upload size={16} /> Import</button>
                        <button type="button" className={BTN_GHOST} onClick={() => setExportOpen(true)}><Download size={16} /> Export</button>
                        <button type="button" className={BTN_PRIMARY} onClick={() => setNewItemSignal((n) => n + 1)}><Plus size={16} /> New item</button>
                    </div>
                ) : <button type="button" className={BTN_PRIMARY} onClick={() => setNewSupplierSignal((n) => n + 1)}><Plus size={16} /> New supplier</button>}
            </div>

            <div role="tablist" aria-label="Catalog" className="flex border-b border-gray-200">
                {tabBtn('items', 'Items')}
                {tabBtn('suppliers', 'Suppliers')}
            </div>

            {tab === 'items'
                ? <CatalogItemsTab key={listKey} orgId={orgId} suppliers={suppliers} showTax={showTax} salesTaxCodes={salesTaxCodes} newSignal={newItemSignal} />
                : <CatalogSuppliersTab suppliers={suppliers} loadError={suppliersError} newSignal={newSupplierSignal} />}
            {exportOpen && <ExportDialog open selection={null} onClose={() => setExportOpen(false)} />}
        </div>
    );
}
